import { lazy, ComponentType } from 'react';

/**
 * Wraps React.lazy with automatic retry logic for transient module fetch failures (e.g. 503).
 * Retries up to `maxRetries` times with exponential backoff before giving up.
 *
 * Cache-busting: when the failure looks like a chunk-load error (typical after a
 * deploy where the previously-hashed chunk URL is gone), retries append a
 * `?cb=<timestamp>` query string to bypass the browser/SW cache. This is done
 * by intercepting fetch for the failing URL during the retry window.
 *
 * On final failure, the error is re-thrown so the nearest ErrorBoundary can render
 * a recovery UI. We intentionally do NOT trigger window.location.reload() here —
 * silent reloads mid-workflow can discard unsaved user input.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  maxRetries = 2
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, maxRetries));
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Failed to fetch dynamically/i.test(
    msg,
  );
}

async function retryImport<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries: number
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (error) {
    if (retries <= 0) {
      console.error('[lazyWithRetry] All retries exhausted:', error);
      throw error;
    }
    const delay = Math.pow(2, 2 - retries) * 500; // 500ms, 1000ms
    const chunkError = isChunkLoadError(error);
    console.warn(
      `[lazyWithRetry] Module load failed${chunkError ? ' (chunk error)' : ''}, retrying in ${delay}ms... (${retries} left)`,
    );
    await new Promise((resolve) => setTimeout(resolve, delay));
    if (chunkError) {
      return retryImportWithCacheBust(factory, retries - 1);
    }
    return retryImport(factory, retries - 1);
  }
}

/**
 * Re-runs the import factory with a temporary fetch shim that appends a
 * cache-busting query string to module/CSS chunk URLs. The shim is removed
 * once the import resolves (success or failure) so the rest of the app is
 * unaffected.
 */
async function retryImportWithCacheBust<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries: number,
): Promise<{ default: T }> {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return retryImport(factory, retries);
  }
  const originalFetch = window.fetch;
  const boundOriginal = originalFetch.bind(window);
  const cb = Date.now().toString();
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
      if (/\.(m?js|css)(\?|$)/i.test(url)) {
        const sep = url.includes('?') ? '&' : '?';
        const busted = `${url}${sep}cb=${cb}`;
        return boundOriginal(busted, init);
      }
    } catch {
      // fall through to original
    }
    return boundOriginal(input as any, init);
  }) as typeof window.fetch;
  try {
    return await retryImport(factory, retries);
  } finally {
    window.fetch = originalFetch;
  }
}
